import { Flex, Input } from "@chakra-ui/react";
import { useState, useRef, useEffect } from "react";

const VerificationInput = ({
  length = 6,
  onChange,
  error = false,
}: {
  length: number;
  onChange: (code: string) => void;
  error?: boolean;
}) => {
  const [code, setCode] = useState<string[]>(new Array(length).fill(""));
  const inputs = useRef<(HTMLInputElement | null)[]>([]);

  // 获取应该聚焦的输入框索引（最后一个已填写数字的下一个位置）
  const getActiveInputIndex = (codeArray: string[]) => {
    const lastFilledIndex = codeArray.findLastIndex((val) => val !== "");
    return lastFilledIndex === -1
      ? 0
      : Math.min(lastFilledIndex + 1, length - 1);
  };

  const focusToActiveInput = () => {
    const activeIndex = getActiveInputIndex(code);
    requestAnimationFrame(() => {
      inputs.current[activeIndex]?.focus();
    });
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
    const value = e.target.value;
    if (isNaN(Number(value))) return;

    const lastChar = value.slice(-1);
    const newCode = [...code];
    newCode[index] = lastChar;
    setCode(newCode);

    onChange(newCode.join(""));

    if (lastChar && index < length - 1) {
      requestAnimationFrame(() => {
        inputs.current[index + 1]?.focus();
      });
    }
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    index: number
  ) => {
    if (e.key === "Backspace") {
      e.preventDefault();

      const lastFilledIndex = code.findLastIndex((val) => val !== "");

      // 如果当前输入框有值，删除当前值
      if (code[index] !== "") {
        const newCode = [...code];
        newCode[index] = "";
        setCode(newCode);
        onChange(newCode.join(""));
      }
      // 如果当前输入框为空且不是第一个输入框，删除前一个值并聚焦到前一个输入框
      else if (index > 0 && code[index - 1] !== "") {
        const newCode = [...code];
        newCode[index - 1] = "";
        setCode(newCode);
        onChange(newCode.join(""));
        requestAnimationFrame(() => {
          inputs.current[index - 1]?.focus();
        });
      }
    }
  };

  const handleInputClick = () => {
    focusToActiveInput();
  };

  const handleFocus = () => {
    focusToActiveInput();
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").slice(0, length);
    const pastedNumbers = pastedData.replace(/[^\d]/g, "").split("");

    const newCode = [...code];
    pastedNumbers.forEach((num, idx) => {
      if (idx < length) {
        newCode[idx] = num;
      }
    });

    setCode(newCode);
    onChange(newCode.join(""));
    focusToActiveInput();
  };

  return (
    <Flex gap="10px" justify="space-between" width="100%">
      {code.map((digit, index) => {
        const isActive = index === getActiveInputIndex(code);
        return (
          <Input
            key={index}
            ref={(el) => (inputs.current[index] = el)}
            type="text"
            inputMode="numeric"
            value={digit}
            onChange={(e) => handleChange(e, index)}
            onKeyDown={(e) => handleKeyDown(e, index)}
            onClick={() => handleInputClick()}
            onFocus={() => handleFocus()}
            onPaste={handlePaste}
            maxLength={1}
            textAlign="center"
            width="45px"
            height="45px"
            bg={error ? "#FDE4C9" : "#FDE4C9"}
            border={error ? "1px solid #FF3B30" : "none"}
            borderRadius="8px"
            fontSize="24px"
            color={error ? "#F13434" : "#333"}
            variant={"unstyled"}
          />
        );
      })}
    </Flex>
  );
};

export default VerificationInput;
